import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { Link, router, usePathname } from 'expo-router';
import { CommonActions } from 'expo-router/react-navigation';

type Leave = (continueLeaving: () => void) => Promise<void>;
type LeaveRegistry = { register: (leave: Leave) => () => void; getLeave: () => Leave | null };
const AppNavigationLeaveContext = createContext<LeaveRegistry | null>(null);

export function AppNavigationLeaveProvider({ children }: { children: React.ReactNode }) {
  const current = useRef<{ token: symbol; leave: Leave } | null>(null);
  const register = useCallback((leave: Leave) => {
    const token = Symbol('focused-checklist');
    current.current = { token, leave };
    return () => { if (current.current?.token === token) current.current = null; };
  }, []);
  const value = useMemo(() => ({ register, getLeave: () => current.current?.leave ?? null }), [register]);
  return <AppNavigationLeaveContext.Provider value={value}>{children}</AppNavigationLeaveContext.Provider>;
}

export const useAppNavigationLeave = () => useContext(AppNavigationLeaveContext);

export function AppNavigationLink({ href, children }: { href: string; children: React.ReactElement }) {
  const registry = useAppNavigationLeave();
  const pathname = usePathname();
  return <Link href={href} asChild onPress={event => {
    const leave = registry?.getLeave();
    const destination = href.replace(/\/\([^/]+\)/g, '') || '/';
    if (!leave || !href.startsWith('/') || href.startsWith('//') || destination === pathname || event.defaultPrevented) return;
    if (Platform.OS === 'web') {
      const mouse = event as React.MouseEvent<HTMLAnchorElement>;
      if (!('button' in mouse) || mouse.button !== 0 || mouse.ctrlKey || mouse.metaKey || mouse.altKey || mouse.shiftKey ||
        ![undefined, null, '', 'self', '_self'].includes(mouse.currentTarget?.target)) return;
    }
    // Cancela o Link antes que ele enfileire a rota; o guard aguarda o flush existente.
    event.preventDefault();
    void leave(() => router.navigate(href));
  }}>{children}</Link>;
}

export function guardAppTabPress(
  event: { defaultPrevented?: boolean; preventDefault: () => void },
  route: { key: string; name: string; params?: object },
  navigation: { getState: () => { key: string; index: number; routes: ReadonlyArray<{ key: string }> }; dispatch: (action: any) => void },
  registry: LeaveRegistry | null
): void {
  const state = navigation.getState();
  const leave = registry?.getLeave();
  if (!leave || event.defaultPrevented || route.name === 'help' || state.routes[state.index]?.key === route.key) return;
  const action = { ...CommonActions.navigate(route), target: state.key };
  event.preventDefault();
  void leave(() => navigation.dispatch(action));
}
